import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import Navbar from "../components/Navbar";
import { Plus, MapPin, Calendar, Trash2, ChevronRight, Compass, Lock, CloudDownload, CloudOff, Loader, Wallet, ListChecks, Globe, ArrowRight, Radio } from "lucide-react";
import { toast } from "sonner";
import { useOfflineCache } from "../hooks/useOfflineCache";

const DEST_COLORS = [
  "from-amber-700 to-orange-800",
  "from-teal-700 to-emerald-900",
  "from-stone-600 to-stone-800",
  "from-rose-700 to-pink-900",
  "from-indigo-700 to-violet-900",
];

const LORA  = { fontFamily: "'Lora', Georgia, serif" };
const SERIF = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

const PENDING_JOB_KEY = "voyage_r_pending_job";

const TOOLS = [
  {
    id: "budget",
    icon: Wallet,
    title: "Budget Voyage",
    desc: "Suivez vos dépenses en temps réel, même sans réseau. Calculateur de solde et convertisseur de devises intégré.",
    href: "/budget",
    testid: "tool-budget",
  },
  {
    id: "checklist",
    icon: ListChecks,
    title: "Ma Valise",
    desc: "Liste de bagages intelligente personnalisée selon votre destination. Ajoutez vos propres éléments.",
    href: "/checklist",
    testid: "tool-checklist",
  },
  {
    id: "culture",
    icon: Globe,
    title: "Guide de Survie Local",
    desc: "Lexique local, guide du pourboire, savoir-vivre et règles de respect de la faune.",
    href: "/culture",
    testid: "tool-culture",
  },
];

function getActiveDestination() {
  try {
    const trip = JSON.parse(localStorage.getItem("voyage_r_active_trip") || "null");
    return trip?.destination || null;
  } catch { return null; }
}

function ToolCard({ tool }) {
  const Icon = tool.icon;
  const dest = getActiveDestination();
  return (
    <div
      className="bg-white border border-[#DCD4C6] flex flex-col overflow-hidden transition-all hover:border-[#C17F59]/50 hover:shadow-sm"
      style={{ borderRadius: "3px" }}
      data-testid={tool.testid}
    >
      {/* Card top accent */}
      <div className="h-1 w-full bg-[#C17F59]/20" />

      <div className="p-5 flex flex-col flex-1">
        {/* Icon + badges */}
        <div className="flex items-start justify-between mb-4">
          <div
            className="w-11 h-11 bg-[#F1EBE1] border border-[#DCD4C6] flex items-center justify-center"
            style={{ borderRadius: "2px" }}
          >
            <Icon size={20} color="#C17F59" strokeWidth={1.5} />
          </div>
          <span
            className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 flex items-center gap-1"
            style={{ ...LORA, borderRadius: "2px" }}
          >
            Hors-ligne
          </span>
        </div>

        {/* Title + desc */}
        <h3 className="text-xl mb-2" style={{ ...SERIF, fontWeight: 600, color: "#2B2623" }}>
          {tool.title}
        </h3>
        <p className="text-sm text-[#5C554E] leading-relaxed flex-1 mb-4" style={LORA}>
          {tool.desc}
        </p>

        {/* Active destination context */}
        {dest && (
          <p
            className="text-xs text-[#8B5E34] italic mb-3 flex items-center gap-1"
            style={LORA}
          >
            <MapPin size={10} strokeWidth={2} />
            Configuré pour {dest}
          </p>
        )}

        {/* CTA */}
        <Link
          to={tool.href}
          className="mt-auto flex items-center justify-between px-4 py-2.5 bg-[#F9F6F0] border border-[#DCD4C6] text-sm text-[#2B2623] hover:bg-[#F1EBE1] hover:border-[#C17F59]/50 transition-all group"
          style={{ ...LORA, borderRadius: "2px", fontWeight: 600 }}
          data-testid={`${tool.testid}-cta`}
        >
          Ouvrir l&apos;outil
          <ArrowRight size={13} strokeWidth={2} className="text-[#C17F59] group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    </div>
  );
}


  // Use server-side flag if available, fallback to client-side calculation
function isAccessExpired(trip) {
  if (trip.access_expired !== undefined) return trip.access_expired;
  if (!trip.date_retour) return false;
  const retour = new Date(trip.date_retour);
  const expiry = new Date(retour.getTime() + 15 * 24 * 60 * 60 * 1000);
  return new Date() > expiry;
}

function getTripStatus(trip) {
  // Unpaid trip expired by backend after 20 days
  if (trip.status === "expiré") return "expiré";
  if (isAccessExpired(trip) && trip.payment_status === "paid") return "expiré";
  if (!trip.admin_validated) return "en validation";
  if (trip.payment_status !== "paid") return "à débloquer";
  // Use server-side trip_status if available
  if (trip.trip_status === "en_cours") return "en_cours";
  // Client-side fallback
  if (trip.date_depart && trip.date_retour) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const depart = new Date(trip.date_depart);
    const retour = new Date(trip.date_retour);
    retour.setHours(23, 59, 59, 999);
    if (today >= depart && today <= retour) return "en_cours";
  }
  return "débloqué";
}

const STATUS_STYLES = {
  "en validation": "bg-amber-100 text-amber-800 border border-amber-200",
  "à débloquer": "bg-[#F1EBE1] text-[#8B5E34] border border-[#DCD4C6]",
  "débloqué": "bg-emerald-50 text-emerald-800 border border-emerald-200",
  "expiré": "bg-gray-100 text-gray-500 border border-gray-200",
  "planifié": "bg-[#F1EBE1] text-[#8B5E34] border border-[#DCD4C6]",
  "en_cours": "bg-emerald-500 text-white border border-emerald-400",
};

const PLAN_BADGE = {
  essentiel:  { label: "Essentiel",    style: "bg-stone-100 text-stone-600 border border-stone-200" },
  confort:    { label: "Confort",      style: "bg-[#FDF3EA] text-[#8B5E34] border border-[#E8CCB0]" },
  serenite:   { label: "Sérénité",     style: "bg-[#2B2623] text-[#F9F6F0] border border-[#2B2623]" },
  _default:   { label: "Accès complet",style: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
};

function TripCard({ trip, onDelete }) {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const bg = DEST_COLORS[trip.destination?.charCodeAt(0) % DEST_COLORS.length] || DEST_COLORS[0];
  const [deleting, setDeleting] = useState(false);
  const status = getTripStatus(trip);
  const expired = status === "expiré";
  const isPaid = trip.payment_status === "paid";

  const nbJours = (() => {
    if (!trip.date_depart || !trip.date_retour) return null;
    const diff = Math.round((new Date(trip.date_retour) - new Date(trip.date_depart)) / 86400000) + 1;
    return diff > 0 ? diff : null;
  })();

  const { cached, caching, cacheTrip } = useOfflineCache(isPaid ? trip.id : null);

  const statusLabel = {
    "en validation": t("dashboard.status_pending"),
    "à débloquer": t("dashboard.status_unlock"),
    "débloqué": t("dashboard.status_unlocked"),
    "expiré": t("dashboard.status_expired"),
    "planifié": t("dashboard.status_planned"),
    "en_cours": "En voyage",
  }[status] || status;

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm(t("dashboard.delete_confirm"))) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/trips/${trip.id}`);
      onDelete(trip.id);
      toast.success(t("dashboard.delete_success"));
    } catch {
      toast.error(t("dashboard.delete_error"));
    } finally {
      setDeleting(false);
    }
  };

  const handleSaveOffline = async (e) => {
    e.stopPropagation();
    if (cached || caching) return;
    const ok = await cacheTrip();
    if (ok) toast.success("Itinéraire sauvegardé — consultable hors-ligne");
    else toast.error("Impossible de sauvegarder hors-ligne pour l'instant");
  };

  return (
    <div
      className="trip-card"
      onClick={() => navigate(`/trip/${trip.id}`)}
      data-testid={`trip-card-${trip.id}`}
    >
      {/* Destination header */}
      <div className={`h-32 bg-gradient-to-br ${bg} relative overflow-hidden`}>
        <div className="absolute inset-0 bg-black/20" />
        {expired && (
          <div className="absolute inset-0 bg-gray-900/50 flex items-center justify-center">
            <Lock size={28} color="white" strokeWidth={1.5} />
          </div>
        )}
        <div className="absolute bottom-3 left-4 right-4 flex items-end justify-between">
          <div>
            <h3
              className="text-white text-xl leading-tight"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600 }}
            >
              {trip.destination}
            </h3>
            {nbJours && (
              <p className="text-white/75 text-xs mt-0.5" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                {nbJours} jour{nbJours > 1 ? "s" : ""}
              </p>
            )}
          </div>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-sm ${STATUS_STYLES[status] || STATUS_STYLES["planifié"]} ${status === "en_cours" ? "flex items-center gap-1.5" : ""}`}>
            {status === "en_cours" && <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse inline-block" />}
            {statusLabel}
          </span>
        </div>
      </div>

      {/* Details */}
      <div className="p-4">
        <div className="flex items-center gap-1.5 text-xs text-[#5C554E] mb-1.5" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          <Calendar size={11} strokeWidth={1.5} className="text-[#C17F59]" />
          {trip.date_depart} — {trip.date_retour}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-[#5C554E]" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          <MapPin size={11} strokeWidth={1.5} className="text-[#C17F59]" />
          {t("dashboard.since")} {trip.ville_depart} · {trip.nb_adultes} {trip.nb_adultes > 1 ? (t("dashboard.travelers") || "voyageurs") : (t("dashboard.traveler") || "voyageur")}
        </div>

        {/* Mode Voyage CTA — en_cours trips only */}
        {status === "en_cours" && (
          <button
            onClick={(e) => { e.stopPropagation(); navigate(`/trip/${trip.id}/live`); }}
            className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm rounded-sm transition-colors"
            style={{ fontFamily: "'Lora', Georgia, serif", fontWeight: 600 }}
            data-testid={`live-mode-btn-${trip.id}`}
          >
            <Radio size={14} strokeWidth={2} className="animate-pulse" />
            Ouvrir le Mode Voyage
          </button>
        )}

        <div className="mt-3 flex items-center justify-between">
          <span
            className="text-xs text-[#C17F59] flex items-center gap-1 group-hover:gap-2 transition-all"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, letterSpacing: "0.03em" }}
          >
            {t("dashboard.see_trip")} <ChevronRight size={12} strokeWidth={2} />
          </span>
          <div className="flex items-center gap-2">
            {isPaid && (() => {
              const badge = PLAN_BADGE[trip.plan_type] || PLAN_BADGE._default;
              return (
                <span
                  className={`text-xs px-2 py-0.5 rounded-sm ${badge.style}`}
                  style={{ fontFamily: "'Lora', Georgia, serif" }}
                  data-testid={`plan-badge-${trip.id}`}
                >
                  {badge.label}
                </span>
              );
            })()}
            {/* Offline save button — paid trips only */}
            {isPaid && (
              <button
                onClick={handleSaveOffline}
                disabled={caching}
                className={`p-1.5 transition-colors ${
                  cached
                    ? "text-emerald-600 cursor-default"
                    : "text-[#9A8F86] hover:text-[#C17F59]"
                }`}
                title={cached ? "Disponible hors-ligne" : "Sauvegarder pour consulter hors-ligne"}
                data-testid={`offline-save-${trip.id}`}
              >
                {caching
                  ? <Loader size={13} strokeWidth={1.5} className="animate-spin" />
                  : cached
                    ? <CloudDownload size={13} strokeWidth={1.5} />
                    : <CloudOff size={13} strokeWidth={1.5} />
                }
              </button>
            )}
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="p-1.5 text-[#9A8F86] hover:text-red-500 transition-colors"
              data-testid={`delete-trip-${trip.id}`}
            >
              <Trash2 size={13} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("voyages");

  useEffect(() => {
    axios
      .get(`${API}/trips`)
      .then(({ data }) => setTrips(Array.isArray(data) ? data : []))
      .catch(() => toast.error("Impossible de charger vos voyages"))
      .finally(() => setLoading(false));
  }, []);

  // Reprise du suivi d'une génération lancée sur la page /plan mais non
  // terminée à temps (génération longue sur mobile). Le job continue côté
  // serveur — on le suit ici et on affiche le voyage dès qu'il est prêt.
  useEffect(() => {
    let pending = null;
    try {
      pending = JSON.parse(localStorage.getItem(PENDING_JOB_KEY) || "null");
    } catch {
      pending = null;
    }
    if (!pending?.job_id) return;
    // Ignore les jobs trop anciens (> 15 min) — probablement abandonnés.
    if (Date.now() - (pending.started_at || 0) > 15 * 60 * 1000) {
      localStorage.removeItem(PENDING_JOB_KEY);
      return;
    }

    let cancelled = false;
    let toastShown = false;

    const poll = async () => {
      let attempts = 0;
      while (!cancelled && attempts < 90) {
        await new Promise((r) => setTimeout(r, 5000));
        attempts++;
        if (cancelled) return;
        let job;
        try {
          job = (await axios.get(`${API}/jobs/${pending.job_id}`)).data;
        } catch {
          continue; // erreur transitoire → on continue
        }
        if (job.status === "complete" && job.trip_id) {
          localStorage.removeItem(PENDING_JOB_KEY);
          if (cancelled) return;
          toast.success("Votre voyage est prêt !");
          try {
            const { data } = await axios.get(`${API}/trips`);
            if (!cancelled) setTrips(Array.isArray(data) ? data : []);
          } catch { /* noop */ }
          return;
        }
        if (job.status === "error") {
          localStorage.removeItem(PENDING_JOB_KEY);
          if (!cancelled) toast.error("La génération a échoué. Veuillez réessayer.");
          return;
        }
        if (!toastShown) {
          toastShown = true;
          toast.message("Votre voyage est en cours de finalisation…");
        }
      }
      if (attempts >= 90) localStorage.removeItem(PENDING_JOB_KEY);
    };

    poll();
    return () => { cancelled = true; };
  }, []);

  const handleDelete = (id) => setTrips((prev) => prev.filter((t) => t.id !== id));

  return (
    <div className="min-h-screen" style={{ background: "#F9F6F0" }}>
      <Navbar />
      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10 animate-fade-in-up">
          <div>
            <p className="section-label mb-2">{t("dashboard.label")}</p>
            <h1
              className="text-4xl sm:text-5xl"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}
            >
              {t("dashboard.title")}
            </h1>
            <p className="text-sm text-[#5C554E] mt-1 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
              {t("dashboard.greeting", { name: user?.name?.split(" ")[0] || "" })}
            </p>
          </div>
          <button
            onClick={() => navigate("/plan")}
            className="btn-primary flex items-center gap-2"
            data-testid="new-trip-btn"
          >
            <Plus size={15} strokeWidth={2} /> {t("dashboard.new_trip")}
          </button>
        </div>

        {/* ── Tab navigation ── */}
        <div className="flex border-b border-[#DCD4C6] mb-8">
          <button
            onClick={() => setActiveTab("voyages")}
            className={`px-4 py-3 text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === "voyages"
                ? "text-[#C17F59] border-b-2 border-[#C17F59] -mb-px"
                : "text-[#9A8F86] hover:text-[#5C554E]"
            }`}
            style={LORA}
            data-testid="tab-voyages"
          >
            Mes Voyages
            {!loading && trips.length > 0 && (
              <span
                className="text-xs bg-[#F1EBE1] text-[#8B5E34] border border-[#DCD4C6] px-1.5 py-0.5"
                style={{ borderRadius: "2px" }}
              >
                {trips.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab("outils")}
            className={`px-4 py-3 text-sm font-medium transition-all flex items-center gap-2 ${
              activeTab === "outils"
                ? "text-[#C17F59] border-b-2 border-[#C17F59] -mb-px"
                : "text-[#9A8F86] hover:text-[#5C554E]"
            }`}
            style={LORA}
            data-testid="tab-outils"
          >
            Mes Outils Voyage
            <span
              className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-1.5 py-0.5"
              style={{ borderRadius: "2px" }}
            >
              3
            </span>
          </button>
        </div>

        {/* ── Tab content ── */}
        {activeTab === "voyages" ? (
          <>
            {loading ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="trip-card animate-pulse">
                    <div className="h-32 bg-[#DCD4C6]" />
                    <div className="p-4 space-y-2">
                      <div className="h-3 bg-[#DCD4C6] rounded w-2/3" />
                      <div className="h-3 bg-[#DCD4C6] rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : trips.length === 0 ? (
              <div className="text-center py-24 animate-fade-in">
                <div className="w-20 h-20 border-2 border-[#DCD4C6] rounded-full flex items-center justify-center mx-auto mb-6">
                  <Compass size={32} className="text-[#C17F59]" strokeWidth={1.5} />
                </div>
                <h2
                  className="text-3xl mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 600, color: "#2B2623" }}
                >
                  {t("dashboard.empty_title")}
                </h2>
                <p className="text-[#5C554E] text-sm mb-8 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                  {t("dashboard.empty_desc")}
                </p>
                <button
                  onClick={() => navigate("/plan")}
                  className="btn-primary inline-flex items-center gap-2"
                  data-testid="empty-state-cta"
                >
                  <Plus size={15} strokeWidth={2} /> {t("dashboard.empty_cta")}
                </button>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-fade-in">
                {trips.map((trip) => (
                  <TripCard key={trip.id} trip={trip} onDelete={handleDelete} />
                ))}
              </div>
            )}
          </>
        ) : (
          /* ── Outils Voyage tab ── */
          <div className="animate-fade-in">
            <p
              className="text-sm text-[#9A8F86] italic mb-6"
              style={{ fontFamily: "'Lora', Georgia, serif" }}
              data-testid="tools-subtitle"
            >
              Trois outils 100 % hors-ligne — disponibles à tout moment, même en mode avion.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tools-grid">
              {TOOLS.map((tool) => (
                <ToolCard key={tool.id} tool={tool} />
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
